// packages/web/src/lib/attentionRoute.ts — R4 (Actionable Everywhere, WS2):
// resolves a Today attention item to WHERE it acts. Every attention tile is a
// DOORWAY (the Today-card taxonomy: a card is either an inline approve/cancel
// decision, or a doorway to the screen where you act) — nothing on Today is
// inert.
//
// The subject is already STRUCTURED on the read model: the attention row
// carries objectType + objectId (packages/server/src/plugins/attention.ts's
// serializeAttentionItem), so we resolve type -> destination directly and never
// have to parse the dedupKey string (e.g. `payment-overdue:<id>`). Two shapes
// of destination:
//   • 'editor' — open the schema-driven record editor on that exact record
//     (the overdue-payment example: openRecordEditor('actor_payment', id), the
//     same door CareerMoney's ledger rows use).
//   • 'navigate' — go to a record's own detail SCREEN (auditions/projects have
//     one) or, when a precise per-record destination isn't available, the
//     relevant list/section by item type (never inert).
// A tile whose subject we can't resolve at all returns null; Today then renders
// it as a plain, non-tappable tile (graceful fallback, no dead handle).
import { routePath } from '../routes/route';
import type { AttentionItem } from './api';

export type AttentionDestination =
  | { kind: 'editor'; typeKey: string; id: string }
  | { kind: 'navigate'; path: string };

// Record types with their OWN detail screen — a tile for one of these navigates
// to that screen (precise), rather than popping the editor overlay.
const DETAIL_SCREEN: Record<string, (id: string) => string> = {
  actor_audition: (id) => routePath({ screen: 'auditionDetail', id }),
  actor_project: (id) => routePath({ screen: 'projectDetail', id }),
};

// Record types the editor overlay can open directly (edit/act on the exact
// record) — the precise destination when there's no dedicated detail screen.
const EDITOR_TYPES = new Set([
  'actor_payment',
  'actor_sides',
  'actor_contract',
  'actor_person',
  'actor_session',
  'actor_shoot_day',
  'actor_review',
]);

// SECTION-LEVEL fallback by item type, for a subject we can't resolve to a
// specific record (no objectType, or a type with no editor/detail screen — e.g.
// a commitment). Routes to the surface the concern lives on, never nowhere.
const SECTION_BY_ITEM_TYPE: Record<string, string> = {
  overdue: routePath({ screen: 'career', section: 'money' }),
  due_soon: routePath({ screen: 'career', section: 'money' }),
  silence: routePath({ screen: 'auditions', stage: 'all' }),
  no_show: routePath({ screen: 'auditions', stage: 'all' }),
  drift: routePath({ screen: 'auditions', stage: 'all' }),
  booking_upcoming: routePath({ screen: 'auditions', stage: 'booked' }),
  instance_due: routePath({ screen: 'auditions', stage: 'all' }),
};

export function resolveAttentionDestination(item: AttentionItem): AttentionDestination | null {
  const { objectType, objectId } = item;
  if (objectType && objectId) {
    const detail = DETAIL_SCREEN[objectType];
    if (detail) return { kind: 'navigate', path: detail(objectId) };
    if (EDITOR_TYPES.has(objectType)) return { kind: 'editor', typeKey: objectType, id: objectId };
  }
  const section = SECTION_BY_ITEM_TYPE[item.itemType];
  if (section) return { kind: 'navigate', path: section };
  return null;
}
